import {
	OrderItemLifecycleStepEnum as Step,
	SampleActionActor,
	type GetOrderQuery,
} from '@codezo/graphql';
import type { FlowRowState, Owner } from '@codezo/order-detail';

type Order = NonNullable<GetOrderQuery['getOrder']>;
type OrderItem = Order['items'][number];
type LifecycleStep = NonNullable<OrderItem['lifecycle']>[number];

/** Look a lifecycle step up by its id. Never by position. */
export function stepOf(item: OrderItem, step: Step): LifecycleStep | undefined {
	return item.lifecycle?.find((s) => s.step === step);
}

/**
 * The lifecycle's own flags, turned into a row state.
 *
 * `skipped` wins: it arrives alongside the other flags and means the step never
 * applied, which must not render as a tick.
 *
 * `outstanding` is the one the backend added once production stopped waiting for
 * payment: the marker can now sit ahead of a step that is still open, so a step
 * that was overtaken reports `outstanding` rather than borrowing `completed`.
 * The three are mutually exclusive on the wire; they are read in the server's
 * own order here so the reading cannot drift if that ever softens.
 */
export function rowState(step: LifecycleStep | undefined): FlowRowState {
	if (!step) return 'upcoming';
	if (step.skipped) return 'skipped';
	if (step.completed) return 'done';
	if (step.current) return 'active';
	if (step.outstanding) return 'outstanding';
	return 'upcoming';
}

/**
 * The state of a row that covers MORE THAN ONE lifecycle step.
 *
 * The design draws "Sample & approval" as one row where the backend has two
 * steps — SAMPLING and APPROVAL. Done only when every step it covers is done or
 * skipped; active if any is; skipped only when all of them are, so a waived
 * sample payment doesn't grey out a sample that is actually being made; and
 * `outstanding` when the run has finished with one of its steps still open.
 */
export function mergedState(steps: (LifecycleStep | undefined)[]): FlowRowState {
	const present = steps.filter(Boolean) as LifecycleStep[];
	if (present.length === 0) return 'upcoming';
	if (present.every((s) => s.skipped)) return 'skipped';
	const real = present.filter((s) => !s.skipped);
	// `current` before `done`: where the item is standing is what a parent row is
	// for, and a passage with something still owed behind it is still running.
	if (real.some((s) => s.current)) return 'active';
	if (real.every((s) => s.completed)) return 'done';
	// Nothing current, nothing left to do, and something never done — the whole
	// passage was overtaken. Saying `done` here is exactly the tick this state
	// exists to stop.
	if (real.some((s) => s.outstanding)) return 'outstanding';
	return 'upcoming';
}

export const asOwner = (actor: SampleActionActor | null | undefined): Owner =>
	actor === SampleActionActor.Admin
		? 'admin'
		: actor === SampleActionActor.Client
			? 'client'
			: null;

/**
 * The first thing a row still owes, in the server's words.
 *
 * A row covering several steps has to be able to say WHICH of them was left
 * open — "Sample & approval" wearing a warn ring and nothing else is a signal
 * with no content, and the reader's next move is to guess. First, not joined:
 * one sentence is a statement, three stacked in a meta slot is a paragraph in a
 * place built for a date.
 *
 * Show it on the ROW only when the row is the only thing on screen.
 *
 * An `active` parent has its panel open, and the sub-step carrying the reason is
 * right there under it — the same sentence twice, two lines apart. An
 * `outstanding` parent is collapsed to a ring and a label, and that is the case
 * this exists for.
 */
export function outstandingNote(
	state: FlowRowState,
	steps: (LifecycleStep | undefined)[]
): string | undefined {
	if (state !== 'outstanding') return undefined;
	for (const step of steps) {
		if (step?.outstanding && step.outstandingReason) return step.outstandingReason;
	}
	return undefined;
}
